(() => {
  if (window.tcgAutumn) return;
  const body = document.body;
  const preference = matchMedia('(prefers-reduced-motion: reduce)');
  const carousels = new Map();
  const observed = new Set();
  let paused = false;
  try { paused = localStorage.getItem('tcgu-autumn-motion') === 'paused'; } catch {}
  const visibility = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => entry.target.classList.toggle('offscreen', !entry.isIntersecting));
  }, { rootMargin: '0px' }) : null;
  const reveals = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      reveals.unobserve(entry.target);
    });
  }, { threshold: 0.03 }) : null;
  function motion() {
    body.classList.toggle('motion-paused', paused || preference.matches);
    body.classList.toggle('tab-hidden', document.hidden);
    document.querySelectorAll('#motion-toggle,[data-motion-mirror]').forEach(button => {
      button.disabled = preference.matches;
      button.setAttribute('aria-pressed', String(paused || preference.matches));
      const label = button.querySelector('#motion-label') || button.querySelector('[data-motion-label]');
      const header = document.querySelector('#motion-toggle');
      const value = preference.matches ? header?.dataset.reducedLabel : paused ? header?.dataset.resumeLabel : header?.dataset.pauseLabel;
      if (label && value) label.textContent = value;
      else if (!label && value) button.textContent = value;
    });
  }
  function initCarousel(root) {
    if (carousels.has(root)) return;
    const controller = new AbortController();
    const on = (target, event, callback, options = {}) => target?.addEventListener(event, callback, { ...options, signal: controller.signal });
    const slides = [...root.querySelectorAll('.feature-slide')];
    const filters = [...root.closest('.tcg-autumn').querySelectorAll('[data-filter]')];
    const dots = root.querySelector('.carousel-dots');
    const count = root.querySelector('.slide-counter');
    const previous = root.querySelector('.carousel-prev');
    const next = root.querySelector('.carousel-next');
    const status = document.createElement('span');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    root.append(status);
    let eligible = slides, index = 0, autoplay;
    function scheduleAutoplay() {
      clearTimeout(autoplay);
      autoplay = setTimeout(() => {
        const bounds = root.getBoundingClientRect();
        if (eligible.length > 1 && !paused && !preference.matches && !document.hidden &&
            !root.matches(':hover, :focus-within') && bounds.bottom > 0 && bounds.top < innerHeight) {
          move(1, false);
        }
        scheduleAutoplay();
      }, 6000);
    }
    function render(announce = true) {
      slides.forEach(slide => { slide.classList.remove('autumn-slide-transition'); slide.hidden = slide !== eligible[index]; slide.inert = slide.hidden; });
      if (announce && eligible[index] && !paused && !preference.matches) {
        void eligible[index].offsetWidth;
        eligible[index].classList.add('autumn-slide-transition');
      }
      [...dots.children].forEach((dot, n) => dot.setAttribute('aria-current', String(n === index)));
      count.setAttribute('aria-live', announce ? 'polite' : 'off');
      count.textContent = eligible.length ? `${String(index + 1).padStart(2, '0')} / ${String(eligible.length).padStart(2, '0')}` : '0 / 0';
      previous.disabled = next.disabled = eligible.length < 2;
      scheduleAutoplay();
      if (announce) status.textContent = eligible[index] ? `${eligible[index].dataset.title} (${index + 1}/${eligible.length})` : '';
    }
    function move(step, announce = true) { if (eligible.length) { index = (index + step + eligible.length) % eligible.length; render(announce); } }
    function filter(value, announce = true) {
      eligible = slides.filter(slide => value === 'all' || slide.dataset.status === value);
      index = 0;
      filters.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.filter === value)));
      dots.replaceChildren(...eligible.map((slide, n) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', slide.dataset.title);
        on(dot, 'click', () => { index = n; render(); });
        return dot;
      }));
      render(announce);
    }
    filters.forEach(button => {
      const total = slides.filter(slide => button.dataset.filter === 'all' || slide.dataset.status === button.dataset.filter).length;
      const label = button.querySelector('span');
      if (label) label.textContent = total;
      button.disabled = total === 0;
      on(button, 'click', () => filter(button.dataset.filter));
    });
    on(previous, 'click', () => move(-1));
    on(next, 'click', () => move(1));
    on(root, 'keydown', event => {
      if (event.target.matches('input,select,textarea')) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
      if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); index = event.key === 'Home' ? 0 : eligible.length - 1; render(); }
    });
    let origin;
    on(root, 'touchstart', event => { origin = event.changedTouches[0]; }, { passive: true });
    on(root, 'touchend', event => {
      if (!origin) return;
      const dx = event.changedTouches[0].clientX - origin.clientX;
      const dy = event.changedTouches[0].clientY - origin.clientY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) move(dx < 0 ? 1 : -1);
      origin = null;
    }, { passive: true });
    filter('all', false);
    carousels.set(root, () => { clearTimeout(autoplay); controller.abort(); status.remove(); });
  }
  function init(scope = document) {
    scope.querySelectorAll('.tcg-autumn .motion-zone').forEach(zone => {
      if (observed.has(zone)) return;
      observed.add(zone);
      visibility?.observe(zone);
    });
    scope.querySelectorAll('.tcg-autumn .reveal').forEach((element, n) => {
      element.style.setProperty('--delay', `${n % 4 * 65}ms`);
      if (reveals) reveals.observe(element); else element.classList.add('is-visible');
    });
    scope.querySelectorAll('.tcg-autumn .carousel').forEach(initCarousel);
    body.classList.add('enhanced');
    motion();
  }
  document.addEventListener('click', event => {
    if (!event.target.closest('#motion-toggle,[data-motion-mirror]')) return;
    paused = !paused;
    try { localStorage.setItem('tcgu-autumn-motion', paused ? 'paused' : 'on'); } catch {}
    motion();
  });
  document.addEventListener('visibilitychange', motion);
  preference.addEventListener('change', motion);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [carousel, destroy] of carousels) if (event.target.contains(carousel)) { destroy(); carousels.delete(carousel); }
    for (const zone of observed) if (event.target.contains(zone)) { visibility?.unobserve(zone); observed.delete(zone); }
    event.target.querySelectorAll('.reveal').forEach(element => reveals?.unobserve(element));
  });
  window.tcgAutumn = { init };
  init();
})();

